/**
 * Assignment-related type definitions
 *
 * This file consolidates all assignment-related types used by the planning UI.
 */

/**
 * Normalized local assignment that can be rendered in both capacity and schedule modes.
 * Stores all the data needed to display the assignment regardless of view mode.
 *
 * - In capacity mode: rows are shift groups, entries show employee names
 * - In schedule mode: rows are employees, entries show shift times
 */
export interface LocalAssignment {
  /** Unique ID for this local assignment */
  id: string;
  /** Employee being assigned */
  employeeId: string;
  /** Employee display name (e.g., "Anna B.") */
  employeeName: string;
  /** Shift being assigned */
  shiftId: string;
  /** Shift name for grouping (e.g., "Morning Shift") */
  shiftName: string;
  /** Shift type for styling */
  shiftType: 'morning' | 'afternoon' | 'night' | 'oncall';
  /** Shift start time display (e.g., "06:00") */
  shiftStartTime: string;
  /** Shift end time display (e.g., "14:00") */
  shiftEndTime: string;
  /** Date of the assignment (ISO date: YYYY-MM-DD) */
  dayKey: string;
}

/**
 * Raw assignment data from API
 */
export interface AssignmentInput {
  id: string;
  shiftId: string;
  employeeId: string;
  status: string;
  /** The date this assignment is for (ISO date string: YYYY-MM-DD) */
  date: string;
  /** Employee-overridden start time for this shift unit on this date (HH:mm). */
  overrideStartTime?: string;
  /** Employee-overridden end time for this shift unit on this date (HH:mm). */
  overrideEndTime?: string;
  /** Employee-overridden location ID for this shift unit on this date. */
  overrideLocationId?: string | null;
  /** Provenance source of this assignment. Present when EffectiveScheduleService is active. */
  source?: 'override' | 'template' | 'person_default' | 'suppressed_default';
}
